import React, { useState, useEffect } from "react";
import { FaHistory, FaFile, FaTimes, FaChevronDown, FaChevronUp, FaCalendarAlt } from "react-icons/fa";
import { formatChatDate, formatChatTime } from "../utils/dateUtils";

const ChatHistory = ({ userId, isOpen, onClose }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [historyMessages, setHistoryMessages] = useState([]);
  const [historyDocuments, setHistoryDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("messages"); // messages or documents
  const [expandedYear, setExpandedYear] = useState(null);

  useEffect(() => {
    if (isOpen && userId) {
      fetchAvailableYears();
    }
  }, [isOpen, userId]);

  const fetchAvailableYears = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/chat/available-years?userId=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch available years");

      const data = await response.json();
      setAvailableYears(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchYearHistory = async (year) => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/chat/archived-history?userId=${userId}&year=${year}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        }
      );

      if (!response.ok) throw new Error(`No history found for ${year}`);

      const data = await response.json();
      setHistoryMessages(data.messages || []);
      setSelectedYear(year);
      setExpandedYear(year);
    } catch (error) {
      setError(error.message);
      setHistoryMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchYearDocuments = async (year) => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/chat/archived-documents?userId=${userId}&year=${year}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        }
      );

      if (!response.ok) throw new Error(`No documents found for ${year}`);

      const data = await response.json();
      setHistoryDocuments(data);
    } catch (error) {
      setError(error.message);
      setHistoryDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleYearClick = (year) => {
    if (expandedYear === year) {
      setExpandedYear(null);
      setSelectedYear(null);
      setHistoryMessages([]);
      setHistoryDocuments([]);
    } else {
      if (viewMode === "messages") {
        fetchYearHistory(year);
      } else {
        fetchYearDocuments(year);
      }
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setExpandedYear(null);
    setSelectedYear(null);
    setHistoryMessages([]);
    setHistoryDocuments([]);
  };

  const truncateFileName = (name) => {
    if (name && name.length > 30) {
      return `${name.substring(0, 15)}...${name.substring(name.length - 10)}`;
    }
    return name;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FaHistory className="text-blue-600 text-xl" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800">Chat History</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <FaTimes className="text-gray-600" />
          </button>
        </div>

        {/* View Mode Toggle */}
        <div className="flex gap-2 p-4 border-b border-gray-200">
          <button
            onClick={() => handleViewModeChange("messages")}
            className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all ${
              viewMode === "messages"
                ? "bg-blue-600 text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Messages
          </button>
          <button
            onClick={() => handleViewModeChange("documents")}
            className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all ${
              viewMode === "documents"
                ? "bg-blue-600 text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Documents
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {error && (
            <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
              {error}
            </div>
          )}

          {!loading && availableYears.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <FaHistory className="mx-auto text-5xl mb-4 opacity-30" />
              <p className="text-lg font-medium">No archived chat history available</p>
              <p className="text-sm mt-2">Chat history will be automatically archived at the end of each year</p>
            </div>
          )}

          {!loading && availableYears.length > 0 && (
            <div className="space-y-3">
              {availableYears.map((yearData) => (
                <div key={yearData.year} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  <button
                    onClick={() => handleYearClick(yearData.year)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <FaCalendarAlt className="text-blue-600 text-xl" />
                      </div>
                      <div className="text-left">
                        <h4 className="font-semibold text-gray-800 text-lg">{yearData.year}</h4>
                        <p className="text-sm text-gray-500">
                          {yearData.messageCount} messages • Archived on {new Date(yearData.archivedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {expandedYear === yearData.year ? (
                      <FaChevronUp className="text-gray-600" />
                    ) : (
                      <FaChevronDown className="text-gray-600" />
                    )}
                  </button>

                  {expandedYear === yearData.year && (
                    <div className="p-4 bg-white max-h-96 overflow-y-auto border-t border-gray-200">
                      {viewMode === "messages" && historyMessages.length > 0 && (
                        <div className="space-y-3">
                          {historyMessages.map((msg, index) => (
                            <div
                              key={index}
                              className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
                            >
                              <div
                                className={`max-w-[80%] p-3 rounded-lg ${
                                  msg.sender === "user"
                                    ? "bg-blue-600 text-white rounded-br-md"
                                    : "bg-gray-100 text-gray-800 rounded-bl-md"
                                }`}
                              >
                                {msg.fileUrl ? (
                                  <div className="flex items-center gap-2">
                                    <FaFile className="text-lg" />
                                    <span className="text-sm">{truncateFileName(msg.originalName)}</span>
                                  </div>
                                ) : (
                                  <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                                )}
                                <div className={`mt-1 text-xs ${msg.sender === "user" ? "text-white/60" : "text-gray-500"}`}>
                                  {formatChatTime(msg.timestamp)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {viewMode === "documents" && historyDocuments.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {historyDocuments.map((doc, index) => (
                            <a
                              key={index}
                              href={`${process.env.REACT_APP_API_BASE_URL}${doc.fileUrl}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-200"
                            >
                              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                <FaFile className="text-blue-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">
                                  {truncateFileName(doc.originalName)}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatChatDate(doc.timestamp)}
                                </p>
                              </div>
                            </a>
                          ))}
                        </div>
                      )}

                      {viewMode === "documents" && historyDocuments.length === 0 && (
                        <div className="text-center py-6 text-gray-500">
                          <FaFile className="mx-auto text-3xl mb-2 opacity-30" />
                          <p>No documents found for {yearData.year}</p>
                        </div>
                      )}

                      {viewMode === "messages" && historyMessages.length === 0 && (
                        <div className="text-center py-6 text-gray-500">
                          <p>No messages found for {yearData.year}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <span className="font-semibold">Note:</span>
            <p>Archived files cannot be removed. Chat transcripts are automatically archived at the end of each year.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatHistory;
